import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysSite } from './queryKeys';

type CountAffectedDlnsOfSiteIdentifierResponse = {
  code: string;
  id: UUID;
  message: string;
};

export type CountAffectedDlnsOfSiteIdentifierData = {
  buildingNumber?: string;
  city?: string;
  country?: string;
  identifier: string;
  ignoreAddressInfo?: boolean;
  postCode?: string;
  streetName?: string;
};

/**
 * Counts the number of affected delivery notes for a given site identifier and supplier.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/count_unaccepted_dlns_site_identifier_new_site_identifier__supplier_id__affected_dlns_count_get
 *
 * @param {UUID} supplierId - The UUID of the supplier.
 * @param {CountAffectedDlnsOfSiteIdentifierData} siteData - Data related to the site identifier.
 * @returns {Promise<CountAffectedDlnsOfSiteIdentifierResponse>} The response containing the count of affected delivery notes.
 */
export const countAffectedDlnsOfSiteIdentifier = async (
  supplierId: UUID,
  siteData: CountAffectedDlnsOfSiteIdentifierData,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.SITE.COUNT_AFFECTED_DLNS(supplierId), {
      searchParams: siteData,
    })
    .json<CountAffectedDlnsOfSiteIdentifierResponse>();

  return response;
};

export const getCountAffectedDlnsOfSiteIdentifierQueryOptions = ({
  supplierId,
  siteData,
}: {
  supplierId: UUID;
  siteData: CountAffectedDlnsOfSiteIdentifierData;
}) => ({
  enabled: Boolean(supplierId) && Boolean(siteData.identifier),
  queryFn: async () => countAffectedDlnsOfSiteIdentifier(supplierId, siteData),
  queryKey: queryKeysSite.getCountAffectedDlnsOfSiteIdentifier(
    supplierId,
    siteData,
  ),
  staleTime: ms('10m'),
});

/**
 * React Query-based custom hook for counting affected delivery notes for a site identifier.
 *
 * @param {UUID} supplierId - The UUID of the supplier.
 * @param {CountAffectedDlnsOfSiteIdentifierData} siteData - Data related to the site identifier.
 * @param {UseQueryOptions<CountAffectedDlnsOfSiteIdentifierResponse>} options - Additional options for the `useQuery` hook.
 * @returns {UseQueryResult<CountAffectedDlnsOfSiteIdentifierResponse>} The query result.
 */
export const useQueryCountAffectedDlnsOfSiteIdentifier = (
  supplierId: UUID,
  siteData: CountAffectedDlnsOfSiteIdentifierData,
  options?: UseQueryOptions,
) =>
  useQuery({
    ...getCountAffectedDlnsOfSiteIdentifierQueryOptions({
      supplierId,
      siteData,
    }),
    ...options,
  });
